import axios from 'axios';
import api from 'api';
import { useAuth } from 'contexts/ProtectedRoute';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactComponent as Documents } from 'assets/icons/B2B/file_copy.svg';
import { ReactComponent as Home } from 'assets/icons/B2B/person.svg';
import { ReactComponent as StartChat } from 'assets/icons/B2B/conversation.svg';
import { ReactComponent as Notes } from 'assets/icons/B2B/sort.svg';
import { BiTrash } from 'react-icons/bi';
import {
    HiBriefcase,
    HiClock,
    HiCheck,
    HiChevronDown,
    HiPlus,
    HiSpeakerphone,
} from 'react-icons/hi';
import { ReactComponent as Notifications } from 'assets/icons/B2B/bell.svg';
import { ReactComponent as Assign } from 'assets/icons/B2B/build.svg';
import { ReactComponent as Onboarding } from 'assets/icons/B2B/group_add.svg';
import { ReactComponent as Reminder } from 'assets/icons/B2B/reminder.svg';
import { ReactComponent as ClipboardIcon } from '../../../../assets/icons/phosphor-clipboard-text.svg';
import Button from '../Button';
import { useWindowSize } from 'hooks/useWindowSize';
import { useCompletedForms } from 'hooks/useCompletedForms';
import { useInvalidateApplicationRetainers } from 'hooks/useApplicationRetainers';
import ROLES from '../../../../assets/userRoles.json';
import PERMISSION from '../../../../assets/permissions.json';
import { FEATURE } from 'constant/features';
import { useHasFeature } from 'hooks/useHasFeature';
import * as S from './QuickActions.styled';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'redux/store';
import { loadApp } from 'redux/application/application.middleware';
import { loadApplications } from 'redux/applications/applications-middleware';
import { setAlert } from 'redux/alert';
import { useTranslation } from 'react-i18next';
import type { VisaDetails } from 'api/rest/application';
import { shouldCollectVisaDetails } from 'helpers/program';
import {
    CLIENT_PAYMENT_STATUSES,
    CLIENT_PAYMENT_STATUS_COLORS,
    CLIENT_PAYMENT_STATUS_LABEL_KEYS,
    normalizeClientPaymentStatus,
} from 'helpers/clientPaymentStatus';
import { caseStatusOptions } from 'data/constants/caseStatus';
import { PaymentStatus } from 'api/models/payload/ClientsPayload';
import RemoveApplicationConfirmationModal from 'views/v2/Dashboards-B2B/components/quickActions/removeApplication/containers';
import TicketsModal from '../BACKOFFICEFLOW/TicketsModal';
import CalendarReminderPage from 'views/v2/calendarReminder/containers';
import FileHistoryModal from 'views/v2/Dashboards-B2B/components/backOfficeFlow/fileHistory/containers';
import EligibilityPage from 'views/v2/Dashboards-B2B/components/eligibility/containers';
// import OnboardingModal from '../CLIENTFLOW/onboarding/Onboarding';
import OnboardingModalV2 from 'views/v2/Dashboards-B2B/components/clientFlow/onboarding/containers';
import StatusDropdown from 'components/common/StatusDropdown/StatusDropdown';
import ResultsAutcomeModal from '../BACKOFFICEFLOW/shared/ResultsOutcomeModal/ResOutcomeModal';
import AssignAgentsModal from 'views/v2/Dashboards-B2B/components/quickActions/assignAgents/containers';
import ReceivedVisaModal from '../BACKOFFICEFLOW/shared/ReceivedVisaModal/ReceivedVisaModal';
// import AssignAgentsModal from './AssignAgentsModal';
import CreateCase from 'views/v2/Dashboards-B2B/components/backOfficeFlow/case/containers/createCase';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import { Fragment } from 'react';
import { TicketStatus } from 'api/models/payload/Ticket';
import { useDashboardView } from '../BACKOFFICEFLOW/MYCLIENTS/components/dashboard/DashboardViewContext';

type StatusOption = {
    label: string;
    color: string;
    value?: string;
};

type ActionButton = {
    name: string;
    color: string;
    isActive?: boolean;
    disabled?: boolean;
    icon?:
        | React.FunctionComponent<
              React.SVGProps<SVGSVGElement> & {
                  title?: string | undefined;
              }
          >
        | string;
    onClick: () => void;
};

type QuickActionsProps = {
    clientView: boolean;
    clientFlow: boolean;
    handleMobileQuickActionsPopoverClose?: () => void;
};

export default function QuickActions({
    clientView,
    clientFlow,
    handleMobileQuickActionsPopoverClose,
}: QuickActionsProps) {
    const dispatch = useDispatch<AppDispatch>();
    const invalidateRetainers = useInvalidateApplicationRetainers();
    const { role } = useAuth();
    const params = useParams();
    const { id: candidateID, language_setting: language } = useSelector(
        (state: RootState) => state.user.entity
    );
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const { t } = useTranslation();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { id: ID } = useParams();
    const navigate = useNavigate();
    const [buttons, setButtons] = useState<ActionButton[]>([]);
    /** Client view only: profile, agreements, results, eligibility — top section above case context. */
    const [clientPrimaryButtons, setClientPrimaryButtons] = useState<ActionButton[]>([]);
    const [isUnreadNotifications, setIsUnreadNotifications] = useState(false);
    const isCandidateAssessment = params['*']?.includes('assessment');
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const hideAssignButton = role === ROLES.sales || role === ROLES.backofficeConsultant;
    const hideRemoveAppButton = role === ROLES.sales;
    const { activeView } = useDashboardView();
    // Hide bulk Assign/Remove on the dashboard when the user is not viewing the
    // Cases table (e.g. Clients or Client Organizations tables).
    const hideBulkDashboardActions = activeView !== 'cases';
    const [removeAppConfirmationModalOpen, setRemoveAppConfirmationModalOpenClose] =
        useState<boolean>(false);
    const applications = useSelector((state: RootState) => state.applications.applications);
    const hasApplications = applications && applications.length > 0;

    const canOnboardOrganization =
        useHasFeature(FEATURE.PARTNER_ORGANIZATIONS) &&
        permissionsList.some((permission) => permission?.name === PERMISSION.ONBOARD_ORGANIZATIONS);

    // Drive the green indicator on the "Client profile" button from the
    // candidate's completed-forms map. `ID` is the candidate id from the
    // /dashboard/:id/... URL when we're in clientView.
    const profileCandidateId = clientView ? Number(ID) : NaN;
    const { allCompleted: allProfileFormsCompleted } = useCompletedForms(
        Number.isFinite(profileCandidateId) ? profileCandidateId : null
    );

    // Case payment status (not paid / partially paid / fully paid).
    const [paymentStatusSaving, setPaymentStatusSaving] = useState(false);
    const paymentStatus = normalizeClientPaymentStatus(activeApp?.payment_status);
    const paymentStatusOptions = useMemo(
        () =>
            CLIENT_PAYMENT_STATUSES.map((status) => ({
                label: t(CLIENT_PAYMENT_STATUS_LABEL_KEYS[status], { lng: language }),
                color: CLIENT_PAYMENT_STATUS_COLORS[status],
                value: status,
            })),
        [language, t]
    );
    const handleSetPaymentStatus = async (status: PaymentStatus) => {
        if (!activeApp) return;
        setPaymentStatusSaving(true);
        try {
            await api.setApplicationPaymentStatus.get(activeApp.id.toString(), status);
            dispatch(loadApp(activeApp.id.toString()));
            dispatch(
                setAlert({
                    message: t('paymentStatusUpdated', { lng: language }),
                    type: 'success',
                })
            );
        } catch {
            dispatch(
                setAlert({
                    message: t('somethingWentWrong', { lng: language }),
                    type: 'error',
                })
            );
        } finally {
            setPaymentStatusSaving(false);
        }
    };
    const handlePaymentStatusSelect = (selected: StatusOption) => {
        const nextValue = selected.value;
        if (!nextValue || !activeApp || nextValue === paymentStatus || paymentStatusSaving) {
            return;
        }
        void handleSetPaymentStatus(nextValue as PaymentStatus);
    };

    // -------------------------------------------------------------------------
    // Case (application) picker — moved here from ClientView so the case
    // selector lives next to the rest of the "case context" controls (status +
    // payment toggle). Shows the selected program / service name, truncated
    // with a tooltip if it doesn't fit, and opens a popover listing the
    // candidate's cases plus a "Create new case" shortcut.
    // -------------------------------------------------------------------------
    const [caseAnchorEl, setCaseAnchorEl] = useState<HTMLButtonElement | null>(null);
    const caseMenuOpen = Boolean(caseAnchorEl);
    const [createCaseOpen, setCreateCaseOpen] = useState<boolean>(false);
    // Only show cases that belong to the candidate currently being viewed —
    // `applications` can briefly hold results for a previously selected client
    // while redux is reloading.
    const candidateApps = useMemo(() => {
        if (!clientView || !ID) return [];
        return (applications || []).filter((app) => app.candidate_id.toString() === ID);
    }, [applications, clientView, ID]);
    useEffect(() => {
        if (!clientView || candidateApps.length === 0) return;
        const activeAppBelongsToCandidate = candidateApps.some((app) => app.id === activeApp?.id);
        if (!activeAppBelongsToCandidate) {
            dispatch(loadApp(candidateApps[0].id.toString()));
        }
    }, [activeApp?.id, candidateApps, clientView, dispatch]);

    const selectedCaseLabel = activeApp?.program_name
        ? activeApp.program_name
        : t('selectClientCase', { lng: language });
    const hasSelectedCase = !!activeApp?.program_name;

    const handleOpenCaseMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setCaseAnchorEl(event.currentTarget);
    };
    const handleCloseCaseMenu = () => setCaseAnchorEl(null);
    const handleSelectCase = (appId: number) => {
        dispatch(loadApp(appId.toString()));
        handleCloseCaseMenu();
    };
    const handleOpenCreateCase = () => {
        setCreateCaseOpen(true);
        handleCloseCaseMenu();
    };
    const handleCloseCreateCase = () => {
        setCreateCaseOpen(false);
        // Refresh the candidate's applications so a newly created case shows
        // up immediately in the menu.
        if (ID) dispatch(loadApplications(ID));
    };

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    useEffect((): void => {
        const fetchIsUnread = async () => {
            try {
                const response = await api.notification.getUnread();
                if (response.status !== 200) {
                    return false;
                }
                setIsUnreadNotifications(response.data['is_unread'] || false);
            } catch (e) {
                console.error(e);
            }
        };
        fetchIsUnread();
    }, []);

    useEffect((): void => {
        const fetchUnreadChats = async (ID: number) => {
            try {
                const response = await api.chatNew.has_unread_messages(ID.toString());
                if (response.status !== 200) {
                    return;
                }
                setHasUnreadMessages(response.data['has_unread_messages'] || false);
            } catch (e: unknown) {
                if (axios.isAxiosError(e) && e.response?.status === 403) {
                    console.log(`View user ${ID} messages: ${e.response.data?.msg}`);
                } else {
                    console.error(e);
                }
            }
        };
        if (activeApp) {
            fetchUnreadChats(activeApp.id);
        }
    }, [activeApp]);

    // ── Fetch open ticket count ────────────────────────────────────────
    const [openTicketCount, setOpenTicketCount] = useState(0);

    useEffect(() => {
        const fetchTicketCount = async () => {
            if (!activeApp?.id) {
                setOpenTicketCount(0);
                return;
            }
            try {
                const response = await api.tickets.getTicketsByApplication(activeApp.id.toString());
                const tickets = response.data || [];
                const openCount = tickets.filter(
                    (t: { status: TicketStatus }) =>
                        t.status === TicketStatus.PENDING || t.status === TicketStatus.IN_REVIEW
                ).length;
                setOpenTicketCount(openCount);
            } catch {
                setOpenTicketCount(0);
            }
        };
        fetchTicketCount();
    }, [activeApp?.id]);

    //depricated - douvle check with Peter

    // const [resultsBtn, setResultsBtn] = useState<boolean>(false);
    // const location = useLocation();

    // useEffect(() => {
    //     const includesVisaProcessPath = location.pathname.includes('/visa-process');
    //     if (includesVisaProcessPath) {
    //         getAssessmentStatus(candidateID);
    //     }
    // }, [candidateID]);

    // const getAssessmentStatus = async (candidate?: number) => {
    //     if (candidate) {
    //         try {
    //             const resp = await api.assessment_payment.getAssessmentPayment(String(candidate));
    //             if (resp.status === 200 || resp.status === 201) {
    //                 if (resp?.data.status === 'SUCCESSFUL') {
    //                     setResultsBtn(true);
    //                 } else {
    //                     setResultsBtn(false);
    //                 }
    //             }
    //         } catch (err) {
    //             console.error(err);
    //         }
    //     }
    // };

    const handleOpenCloseRemoveAppModal = () => {
        setRemoveAppConfirmationModalOpenClose((state) => !state);
    };

    const clientFlowButtons = [
        {
            name: `${t('myProfile', { lng: language })}`,
            color: 'purple',
            icon: Home,
            isActive: params['*'] ? params['*'].endsWith('/assessment') : false,
            onClick: () => {
                navigate(`/visa-process/assessment`);
            },
            show: permissionsList.some((permission) => permission?.name === PERMISSION?.EDIT),
        },
        {
            name: `${t('documents', { lng: language })}`,
            color: 'purple',
            icon: Documents,
            isActive: params['*'] ? params['*'].endsWith('/documents') : false,
            onClick: () => {
                navigate(`/visa-process/${ID}/documents`);
            },
            show: !isCandidateAssessment,
            disabled: !hasApplications,
        },

        {
            name: `${t('chats', { lng: language })}`,
            color: 'purple',
            icon: StartChat,
            isActive: params['*'] ? params['*'].endsWith('/chat') : false,
            onClick: () => {
                navigate(`/visa-process/${ID}/chats`);
                setHasUnreadMessages(false);
            },
            show: hasApplications,
            disabled: !hasApplications,
        },
        {
            name: `${t('agreement', { lng: language })}`,
            color: 'purple',
            icon: Notes,
            isActive: params['*'] ? params['*'].endsWith('/agreement') : false,
            onClick: () => {
                navigate(`/visa-process/${ID}/agreement`);
            },
            show: !isCandidateAssessment,
        },
        {
            name: `${t('report', { lng: language })}`,
            color: 'purple',
            icon: ClipboardIcon,
            isActive: params['*'] ? params['*'].endsWith('/results') : false,
            onClick: () => {
                navigate(`/visa-process/${ID}/results`);
            },
            show: permissionsList.some((permission) => permission?.name === PERMISSION?.VIEW),
        },
    ];

    const [showEligibilityPage, setShowEligibilityPage] = useState(false);
    const handleCloseEligibilityPage = () => setShowEligibilityPage(false);

    const [showOnboardingPage, setShowOnboardingPage] = useState(false);
    const handleCloseOnboardingPage = async () => {
        setShowOnboardingPage((prevState) => !prevState);
    };

    const [showReminderPage, setShowReminderPage] = useState(false);
    const handleCloseReminderPage = () => setShowReminderPage(false);

    const [fileHistoryModalOpen, setFileHistoryModalOpen] = useState(false);
    const [ticketsModalOpen, setTicketsModalOpen] = useState(false);

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignModalAppIds, setAssignModalAppIds] = useState<number[]>([]);
    const [assignModalRefreshMode, setAssignModalRefreshMode] = useState<'app' | 'clients'>('app');
    const handleCloseAssignModal = () => setShowAssignModal(false);

    const openBulkAssignModal = () => {
        const storedApps = localStorage.getItem('selectedApps');
        const storedAppsToAssign: number[] = storedApps ? JSON.parse(storedApps) : [];
        setAssignModalAppIds(storedAppsToAssign);
        setAssignModalRefreshMode('clients');
        setShowAssignModal(true);
    };

    const openClientAssignModal = () => {
        setAssignModalAppIds(activeApp ? [activeApp.id] : []);
        setAssignModalRefreshMode('app');
        setShowAssignModal(true);
    };

    const [showResultsOutcomeModal, setShowResultsOutcomeModal] = useState(false);
    const [showReceivedVisaModal, setShowReceivedVisaModal] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<string>('');

    const saveNewStatus = async (
        nextStatus: string,
        visaResult?: {
            visa_result: 'POSITIVE' | 'NEGATIVE';
            visa?: VisaDetails;
        }
    ) => {
        if (!activeApp) return;

        await api.application.setClientApplicationStatus(activeApp.id.toString(), {
            client_status: nextStatus,
            ...visaResult,
        });
        dispatch(loadApp(activeApp.id.toString()));
        // TWO_STAGE generates the visa retainer when the case reaches In review
        invalidateRetainers(activeApp.id);
        setShowResultsOutcomeModal(false);
    };

    const handleStatusSelect = (selected: StatusOption) => {
        const nextValue = selected.value ?? '';
        if (!nextValue || !activeApp || nextValue === activeApp.client_status) return;
        if (nextValue === 'VISA_RESULTS') {
            setPendingStatus(nextValue);
            setShowResultsOutcomeModal(true);
        } else {
            void saveNewStatus(nextValue).catch(console.error);
        }
    };

    const handleConfirmedResOutcome = async (value: 'success' | 'fail') => {
        if (!activeApp || !pendingStatus) return;

        const collectVisaDetails = shouldCollectVisaDetails(activeApp.program_name);

        if (value === 'success' && collectVisaDetails) {
            setShowResultsOutcomeModal(false);
            setShowReceivedVisaModal(true);
            return;
        }

        try {
            const resp = await api.application.patch(activeApp.id.toString(), { status: value });
            if (resp?.status === 200 || resp?.status === 201) {
                await saveNewStatus(pendingStatus, {
                    visa_result: value === 'success' ? 'POSITIVE' : 'NEGATIVE',
                });
            }
            setPendingStatus('');
        } catch (err) {
            console.error(err);
            setShowResultsOutcomeModal(false);
        }
    };

    const handleVisaReceived = async (visa: VisaDetails) => {
        if (!activeApp || !pendingStatus) return;

        const response = await api.application.patch(activeApp.id.toString(), {
            status: 'success',
        });
        if (response?.status !== 200 && response?.status !== 201) {
            throw new Error('Unable to update the application result.');
        }

        await saveNewStatus(pendingStatus, {
            visa_result: 'POSITIVE',
            visa,
        });
        setPendingStatus('');
        setShowReceivedVisaModal(false);
    };

    useEffect(() => {
        // if (resultsBtn) {
        //     clientFlowButtons.push(newObj);
        // }

        if (clientView) {
            setClientPrimaryButtons([
                {
                    name: `${t('clientProfile', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? Home : '',
                    isActive: params['*'] ? params['*'].endsWith('/client-profile') : false,
                    onClick: () => {
                        navigate(`/dashboard/${ID}/client-profile`);
                    },
                },
                {
                    name: `${t('agreements', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? Documents : '',
                    isActive: params['*'] ? params['*'].endsWith('/agreement') : false,
                    onClick: () => {
                        navigate(`/dashboard/${ID}/client-profile/agreement`);
                    },
                },
                {
                    name: `${t('results', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? ClipboardIcon : '',
                    isActive: params['*'] ? params['*'].endsWith('/results') : false,
                    onClick: () => {
                        navigate(`/dashboard/${ID}/client-profile/results`);
                    },
                },
                {
                    name: `${t('eligibility', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? ClipboardIcon : '',
                    isActive: params['*'] ? params['*'].endsWith('/eligibility') : false,
                    onClick: () => {
                        setShowEligibilityPage(true);
                        handleMobileQuickActionsPopoverClose &&
                            handleMobileQuickActionsPopoverClose();
                    },
                },
            ]);
            setButtons([
                {
                    name: `${t('documents', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? Documents : '',
                    isActive: params['*'] ? params['*'].endsWith('/documents') : false,
                    onClick: () => {
                        navigate(`/dashboard/${ID}/client-profile/documents`);
                    },
                },
                {
                    name: `${t('chats', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? StartChat : '',
                    isActive: params['*'] ? params['*'].endsWith('/chat') : false,
                    onClick: () => {
                        navigate(`/dashboard/${ID}/client-profile/chat`);
                        setHasUnreadMessages(false);
                    },
                },
                {
                    name: `${t('addReminder', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? Reminder : '',
                    isActive: false,
                    onClick: () => {
                        setShowReminderPage(true);
                        handleMobileQuickActionsPopoverClose &&
                            handleMobileQuickActionsPopoverClose();
                    },
                },
                {
                    name: `${t('notes', { lng: language })}`,
                    color: 'purple',
                    icon: language === 'EN' ? Notes : '',
                    isActive: params['*']
                        ? params['*'].endsWith('/notes') ||
                          params['*'].endsWith('/new') ||
                          params['*'].endsWith('/edit') ||
                          params['*'].endsWith('/save')
                        : false,
                    onClick: () => {
                        navigate(`/dashboard/${ID}/client-profile/notes`);
                    },
                },
            ]);
        } else if (clientFlow) {
            setClientPrimaryButtons([]);
            setButtons(clientFlowButtons.filter((btn) => btn.show));
        } else if (role === ROLES.sales) {
            setClientPrimaryButtons([]);
            setButtons([
                {
                    name: `${t('onboarding', { lng: language })}`,
                    color: 'purple',
                    icon: Onboarding,
                    isActive: params['*'] ? params['*'].includes('/sales-onboarding') : false,
                    onClick: () => {
                        if (canOnboardOrganization) {
                            setShowOnboardingPage(true);
                        } else {
                            navigate(`/dashboard/sales-onboarding`);
                        }
                    },
                },
            ]);
        } else {
            setClientPrimaryButtons([]);
            setButtons([
                {
                    name: `${t('notifications', { lng: language })}`,
                    color: 'purple',
                    icon: Notifications,
                    isActive: params['*'] ? params['*'].endsWith('/notifications') : false,
                    onClick: () => {
                        navigate(`/dashboard/notifications`);
                        setIsUnreadNotifications(false);
                    },
                },
                {
                    name: `${t('onboarding', { lng: language })}`,
                    color: 'purple',
                    icon: Onboarding,
                    isActive: params['*'] ? params['*'].includes('/sales-onboarding') : false,
                    onClick: () => {
                        setShowOnboardingPage(true);
                    },
                },
            ]);
        }

        //eslint-disable-next-line
    }, [
        clientView,
        clientFlow,
        candidateID,
        params,
        role,
        permissionsList,
        canOnboardOrganization,
    ]);

    const quickActionsSurfaceStyle = {
        ...(handleMobileQuickActionsPopoverClose
            ? { width: 'min(420px, calc(100vw - 48px))' as const }
            : { width: '100%' as const }),
    };

    return (
        <>
            {clientView ? (
                <S.ActionsContainer style={quickActionsSurfaceStyle}>
                    <S.TitleWrapper>
                        <S.Eyebrow>
                            {t('quickActions', { lng: language, defaultValue: 'Quick Actions' })}
                        </S.Eyebrow>
                    </S.TitleWrapper>
                    <S.ContextGrid>
                        <S.CaseDropdownButton
                            type="button"
                            onClick={handleOpenCaseMenu}
                            aria-haspopup="listbox"
                            aria-expanded={caseMenuOpen}
                            isActive={caseMenuOpen}
                            title={hasSelectedCase ? selectedCaseLabel : undefined}
                        >
                            <S.CaseDropdownIcon>
                                <HiBriefcase />
                            </S.CaseDropdownIcon>
                            <S.CaseDropdownBody>
                                <S.CaseDropdownLabel>
                                    {t('case', { lng: language, defaultValue: 'Case' })}
                                </S.CaseDropdownLabel>
                                <S.CaseDropdownValue>{selectedCaseLabel}</S.CaseDropdownValue>
                            </S.CaseDropdownBody>
                            <S.CaseDropdownChevron open={caseMenuOpen}>
                                <HiChevronDown />
                            </S.CaseDropdownChevron>
                        </S.CaseDropdownButton>
                        <S.StatusSlot>
                            <StatusDropdown
                                key={activeApp?.client_status || 'not-assigned'}
                                options={caseStatusOptions}
                                initialValue={
                                    activeApp?.client_status
                                        ? activeApp.client_status
                                        : 'NOT_ASSIGNED'
                                }
                                onChange={handleStatusSelect}
                            />
                        </S.StatusSlot>
                        <S.StatusSlot
                            title={
                                activeApp
                                    ? t(CLIENT_PAYMENT_STATUS_LABEL_KEYS[paymentStatus], {
                                          lng: language,
                                      })
                                    : t('selectCaseForPayment', {
                                          lng: language,
                                          defaultValue: 'Select a case to update payment status',
                                      })
                            }
                        >
                            <StatusDropdown
                                key={paymentStatus}
                                options={paymentStatusOptions}
                                initialValue={paymentStatus}
                                disabled={!activeApp || paymentStatusSaving}
                                onChange={handlePaymentStatusSelect}
                            />
                        </S.StatusSlot>
                    </S.ContextGrid>
                    <S.ActionsButtonContainer>
                        {clientPrimaryButtons.map((button, i) => {
                            const isClientProfileButton =
                                button.name === t('clientProfile', { lng: language });
                            return (
                                <Fragment key={`qa-primary-${i}`}>
                                    <Button
                                        isActive={button.isActive}
                                        color={button.color}
                                        border={button.color}
                                        onClick={() => {
                                            button.onClick();
                                            handleMobileQuickActionsPopoverClose &&
                                                handleMobileQuickActionsPopoverClose();
                                        }}
                                        style={{ width: '100%' }}
                                        aria-current={button.isActive ? 'page' : undefined}
                                        isUnreadNotifications={isUnreadNotifications}
                                        isUnreadMessages={hasUnreadMessages}
                                    >
                                        {button.icon && (
                                            <button.icon
                                                style={{ width: '18px', height: '18px' }}
                                            />
                                        )}
                                        {button.name}
                                        {isClientProfileButton && allProfileFormsCompleted && (
                                            <S.CompletedDot
                                                aria-label={t('allFormsCompleted', {
                                                    lng: language,
                                                    defaultValue: 'All forms completed',
                                                })}
                                                title={t('allFormsCompleted', {
                                                    lng: language,
                                                    defaultValue: 'All forms completed',
                                                })}
                                            >
                                                <HiCheck size={10} />
                                            </S.CompletedDot>
                                        )}
                                    </Button>
                                </Fragment>
                            );
                        })}
                        {buttons &&
                            buttons.map((button, i) => {
                                const isClientProfileButton =
                                    button.name === t('clientProfile', { lng: language });
                                return (
                                    <Fragment key={`qa-context-${i}`}>
                                        <Button
                                            isActive={button.isActive}
                                            color={button.color}
                                            border={button.color}
                                            onClick={() => {
                                                button.onClick();
                                                handleMobileQuickActionsPopoverClose &&
                                                    handleMobileQuickActionsPopoverClose();
                                            }}
                                            style={{ width: '100%' }}
                                            aria-current={button.isActive ? 'page' : undefined}
                                            isUnreadNotifications={isUnreadNotifications}
                                            isUnreadMessages={hasUnreadMessages}
                                        >
                                            {button.icon && (
                                                <button.icon
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                            )}
                                            {button.name}
                                            {isClientProfileButton && allProfileFormsCompleted && (
                                                <S.CompletedDot
                                                    aria-label={t('allFormsCompleted', {
                                                        lng: language,
                                                        defaultValue: 'All forms completed',
                                                    })}
                                                    title={t('allFormsCompleted', {
                                                        lng: language,
                                                        defaultValue: 'All forms completed',
                                                    })}
                                                >
                                                    <HiCheck size={10} />
                                                </S.CompletedDot>
                                            )}
                                        </Button>
                                    </Fragment>
                                );
                            })}
                        <Button
                            color="purple"
                            border="purple"
                            disabled={!activeApp}
                            title={
                                !activeApp
                                    ? t('selectCaseForFileHistory', {
                                          lng: language,
                                          defaultValue: 'Select a case to view file history',
                                      })
                                    : undefined
                            }
                            onClick={() => {
                                if (!activeApp) return;
                                setFileHistoryModalOpen(true);
                                handleMobileQuickActionsPopoverClose &&
                                    handleMobileQuickActionsPopoverClose();
                            }}
                            style={{ width: '100%' }}
                        >
                            {language === 'EN' && (
                                <HiClock style={{ width: '18px', height: '18px' }} />
                            )}
                            {t('fileHistory', { lng: language })}
                        </Button>
                        <Button
                            color="purple"
                            border="purple"
                            disabled={!activeApp}
                            title={
                                !activeApp
                                    ? t('selectCaseForTickets', {
                                          lng: language,
                                          defaultValue: 'Select a case to view tickets',
                                      })
                                    : undefined
                            }
                            onClick={() => {
                                if (!activeApp) return;
                                navigate(`/dashboard/${ID}/client-profile/tickets`);
                            }}
                            style={{ width: '100%', position: 'relative' }}
                        >
                            {language === 'EN' && (
                                <HiSpeakerphone style={{ width: '18px', height: '18px' }} />
                            )}
                            {t('tickets', { lng: language })}
                            {openTicketCount > 0 && (
                                <Badge
                                    badgeContent={openTicketCount}
                                    color="error"
                                    max={99}
                                    sx={{ position: 'absolute', top: 6, right: 6 }}
                                />
                            )}
                        </Button>
                        {!hideAssignButton && (
                            <Button
                                color="purple"
                                border="purple"
                                onClick={() => {
                                    openClientAssignModal();
                                    handleMobileQuickActionsPopoverClose &&
                                        handleMobileQuickActionsPopoverClose();
                                }}
                                style={{ width: '100%' }}
                            >
                                {language === 'EN' && (
                                    <Assign style={{ width: '18px', height: '18px' }} />
                                )}
                                {t('assign', { lng: language })}
                            </Button>
                        )}
                        {!hideRemoveAppButton && (
                            <Button
                                color="red"
                                border="red"
                                onClick={() => handleOpenCloseRemoveAppModal()}
                                style={{ width: '100%' }}
                            >
                                {language === 'EN' && (
                                    <BiTrash style={{ width: '18px', height: '18px' }} />
                                )}
                                {t('removeCase', { lng: language })}
                            </Button>
                        )}
                    </S.ActionsButtonContainer>
                    <Popover
                        open={caseMenuOpen}
                        anchorEl={caseAnchorEl}
                        onClose={handleCloseCaseMenu}
                        marginThreshold={0}
                        sx={{ zIndex: 9200 }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: '6px',
                                    overflow: 'visible',
                                    background: 'transparent',
                                    boxShadow: 'none',
                                },
                            },
                        }}
                    >
                        <S.CaseMenu
                            style={{
                                width: caseAnchorEl ? `${caseAnchorEl.offsetWidth}px` : undefined,
                            }}
                        >
                            <S.CaseMenuCreate type="button" onClick={handleOpenCreateCase}>
                                <HiPlus size={16} />
                                <S.CaseMenuItemLabel>
                                    {t('createNewCase', { lng: language })}
                                </S.CaseMenuItemLabel>
                            </S.CaseMenuCreate>
                            {candidateApps.length > 0 && <S.CaseMenuDivider />}
                            {candidateApps.length === 0 ? (
                                <S.CaseMenuEmpty>
                                    {t('noCasesYet', {
                                        lng: language,
                                        defaultValue: 'No cases yet',
                                    })}
                                </S.CaseMenuEmpty>
                            ) : (
                                candidateApps.map((app) => {
                                    const selected = activeApp?.id === app.id;
                                    return (
                                        <S.CaseMenuItem
                                            key={app.id}
                                            type="button"
                                            selected={selected}
                                            onClick={() => handleSelectCase(app.id)}
                                            title={app.program_name}
                                        >
                                            {selected && <HiCheck size={14} />}
                                            <S.CaseMenuItemLabel>
                                                {app.program_name}
                                            </S.CaseMenuItemLabel>
                                        </S.CaseMenuItem>
                                    );
                                })
                            )}
                        </S.CaseMenu>
                    </Popover>
                </S.ActionsContainer>
            ) : (
                <S.ActionsContainer style={quickActionsSurfaceStyle}>
                    <S.TitleWrapper>
                        <S.Eyebrow>
                            {t('quickActions', { lng: language, defaultValue: 'Quick Actions' })}
                        </S.Eyebrow>
                    </S.TitleWrapper>
                    <S.ActionsButtonContainer>
                        {!clientView &&
                            buttons &&
                            buttons.map((button, i) => {
                                const isClientProfileButton =
                                    button.name === t('clientProfile', { lng: language });
                                return (
                                    <Fragment key={i}>
                                        <Button
                                            isActive={button.isActive}
                                            color={button.color}
                                            border={button.color}
                                            onClick={() => {
                                                button.onClick();
                                                handleMobileQuickActionsPopoverClose &&
                                                    handleMobileQuickActionsPopoverClose();
                                            }}
                                            style={{ width: '100%' }}
                                            aria-current={button.isActive ? 'page' : undefined}
                                            isUnreadNotifications={isUnreadNotifications}
                                            isUnreadMessages={hasUnreadMessages}
                                        >
                                            {button.icon && (
                                                <button.icon
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                            )}
                                            {button.name}
                                            {isClientProfileButton && allProfileFormsCompleted && (
                                                <S.CompletedDot
                                                    aria-label={t('allFormsCompleted', {
                                                        lng: language,
                                                        defaultValue: 'All forms completed',
                                                    })}
                                                    title={t('allFormsCompleted', {
                                                        lng: language,
                                                        defaultValue: 'All forms completed',
                                                    })}
                                                >
                                                    <HiCheck size={10} />
                                                </S.CompletedDot>
                                            )}
                                        </Button>
                                    </Fragment>
                                );
                            })}
                        {mobile ? (
                            <>
                                {clientView ? (
                                    <>{/* this is necessary, don't remove */}</>
                                ) : clientFlow ? (
                                    <> {/* this is necessary, don't remove */}</>
                                ) : (
                                    <>
                                        {params['*'] &&
                                            params['*'].endsWith('dashboard') &&
                                            !hideAssignButton &&
                                            !hideBulkDashboardActions && (
                                                <Button
                                                    color="purple"
                                                    border="purple"
                                                    onClick={() => {
                                                        openBulkAssignModal();
                                                        handleMobileQuickActionsPopoverClose &&
                                                            handleMobileQuickActionsPopoverClose();
                                                    }}
                                                    style={{ width: '100%' }}
                                                >
                                                    {language === 'EN' && (
                                                        <Assign
                                                            style={{
                                                                width: '18px',
                                                                height: '18px',
                                                            }}
                                                        />
                                                    )}
                                                    {t('assign', { lng: language })}
                                                </Button>
                                            )}
                                        {params['*'] &&
                                            params['*'].endsWith('dashboard') &&
                                            !hideRemoveAppButton &&
                                            !hideBulkDashboardActions && (
                                                <Button
                                                    color="red"
                                                    border="red"
                                                    onClick={() => handleOpenCloseRemoveAppModal()}
                                                    style={{ width: '100%' }}
                                                >
                                                    <BiTrash
                                                        style={{ width: '18px', height: '18px' }}
                                                    />
                                                    {t('remove', { lng: language })}
                                                </Button>
                                            )}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {params['*'] &&
                                    params['*'].endsWith('dashboard') &&
                                    !hideAssignButton &&
                                    !hideBulkDashboardActions && (
                                        <Button
                                            color="purple"
                                            border="purple"
                                            onClick={() => openBulkAssignModal()}
                                            style={{ width: '100%' }}
                                        >
                                            <Assign style={{ width: '18px', height: '18px' }} />
                                            {t('assign', { lng: language })}
                                        </Button>
                                    )}
                                {params['*'] &&
                                    params['*'].endsWith('dashboard') &&
                                    !hideRemoveAppButton &&
                                    !hideBulkDashboardActions && (
                                        <Button
                                            color="red"
                                            border="red"
                                            onClick={() => handleOpenCloseRemoveAppModal()}
                                            style={{ width: '100%' }}
                                        >
                                            <BiTrash style={{ width: '18px', height: '18px' }} />
                                            {t('remove', { lng: language })}
                                        </Button>
                                    )}
                            </>
                        )}
                    </S.ActionsButtonContainer>
                </S.ActionsContainer>
            )}
            <RemoveApplicationConfirmationModal
                toggleRemoveAppModal={handleOpenCloseRemoveAppModal}
                open={removeAppConfirmationModalOpen}
                mobile={mobile}
                clientView={clientView}
                closeMobileQuickActions={handleMobileQuickActionsPopoverClose}
            />
            {showReminderPage && (
                <CalendarReminderPage
                    onClose={handleCloseReminderPage}
                    appId={activeApp?.id.toString() || ''}
                />
            )}
            <TicketsModal open={ticketsModalOpen} onClose={() => setTicketsModalOpen(false)} />
            {showAssignModal && (
                <AssignAgentsModal
                    onClose={handleCloseAssignModal}
                    appIds={assignModalAppIds}
                    refreshMode={assignModalRefreshMode}
                />
            )}
            {/* <OnboardingModal
                open={showOnboardingPage}
                onClose={handleCloseOnboardingPage}
                appId={activeApp?.id.toString() || ''}
            /> */}
            <OnboardingModalV2
                open={showOnboardingPage}
                onClose={handleCloseOnboardingPage}
                appId={activeApp?.id.toString() || ''}
            />
            {showEligibilityPage && (
                <EligibilityPage
                    onClose={handleCloseEligibilityPage}
                    appId={activeApp?.id.toString() || ''}
                />
            )}
            <ResultsAutcomeModal
                open={showResultsOutcomeModal}
                onClose={() => {
                    setShowResultsOutcomeModal(false);
                    setPendingStatus('');
                }}
                onConfirm={handleConfirmedResOutcome}
            />
            <ReceivedVisaModal
                open={showReceivedVisaModal}
                application={activeApp}
                onClose={() => {
                    setShowReceivedVisaModal(false);
                    setPendingStatus('');
                }}
                onVisaReceived={handleVisaReceived}
            />
            <FileHistoryModal
                open={fileHistoryModalOpen}
                onClose={() => setFileHistoryModalOpen(false)}
                caseId={activeApp?.id.toString() ?? null}
                clientId={activeApp?.candidate_id ?? null}
            />
            <CreateCase open={createCaseOpen} candidateId={ID} onClose={handleCloseCreateCase} />
        </>
    );
}
